import React, { useEffect, useMemo, useState } from 'react';

import { MemberContactRankingType } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  CareTeamMember,
  ContactInfoRank,
} from '@vestahealthcare/common/models';

import { Modal } from 'styleguide-v2';

import { useSelector } from 'dash/src/redux/store';
import { fetchMemberCareTeamMembers } from 'dash/src/services/CareTeamServices';

import { showGlobalError } from '../../../components/GlobalMessage';
import {
  fetchMemberContactInfoRank,
  updateMemberContactInfoRank,
} from '../../../services/PatientServices';
import { ContactInfoCTMRow } from './components/ContactInfoCTMRow';
import { ContactInfoMemberRow } from './components/ContactInfoMemberRow';
import { ContactInfoSortableList } from './components/ContactInfoSortableList';
import { MemberType } from './components/types';

interface Props {
  careTeamMembers?: CareTeamMember[];
  member: MemberType;
  onClose: () => void;
  open?: boolean;
  getCareTeam?: boolean;
}

type MemberRanked = MemberType & { rank?: number; rankId?: number };
type CareTeamMemberRanked = CareTeamMember & { rank?: number; rankId?: number };

export const ContactInfoModal = (props: Props) => {
  const {
    careTeamMembers: careTeamMembersParam,
    member,
    open,
    onClose,
  } = props;

  const [editMode, setEditMode] = useState<boolean>(false);

  const [careTeamMembersFetch, setCareTeamMembersFetch] = useState<
    CareTeamMember[]
  >([]);
  const [contactInfoRanks, setContactInfoRanks] = useState<ContactInfoRank[]>();

  const careTeamMembersState = useSelector(
    (state) => state.memberInfoSlice.careTeamMembers,
  );

  const careTeamMembers = useMemo(() => {
    let result: CareTeamMember[] = [];
    if (careTeamMembersParam) result = careTeamMembersParam;
    else if (props.getCareTeam) result = careTeamMembersFetch;
    else result = careTeamMembersState;

    return result.toSorted(CareTeamMember.sort);
  }, [
    props.getCareTeam,
    careTeamMembersFetch,
    careTeamMembersState,
    careTeamMembersParam,
  ]);

  const [items, setItems] = useState<(MemberRanked | CareTeamMemberRanked)[]>(
    [],
  );

  const getCTMembers = async () => {
    const { items: members } = await fetchMemberCareTeamMembers(member.id);
    setCareTeamMembersFetch(members);
  };

  const getContactRankInfo = async () => {
    try {
      const ranks = await fetchMemberContactInfoRank(member.id);
      setContactInfoRanks(ranks);
    } catch (e) {
      showGlobalError(e as string);
      setContactInfoRanks([]);
    }
  };

  useEffect(() => {
    if (props.getCareTeam) {
      getCTMembers();
    }
    getContactRankInfo();
  }, [member?.id]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => setEditMode(false), 250);
    }
  }, [open]);

  const resetItems = () => {
    let result: (MemberRanked | CareTeamMemberRanked)[] = [
      member,
      ...(careTeamMembers || []).filter(({ active }) => active),
    ];
    if (contactInfoRanks?.length) {
      result = result
        .map((item: MemberRanked | CareTeamMemberRanked) => {
          let rank: ContactInfoRank | undefined;
          if (item instanceof CareTeamMember) {
            rank = contactInfoRanks.find(
              (rank) =>
                rank.type === MemberContactRankingType.CARE_TEAM_MEMBER &&
                rank.careTeamMemberId === item.id,
            );
          } else {
            rank = contactInfoRanks.find(
              (rank) =>
                rank.type === MemberContactRankingType.MEMBER &&
                rank.memberId === item.id,
            );
          }
          item.rank = rank?.ranking;
          item.rankId = rank?.id;

          return item;
        })
        .toSorted(
          (
            { rank: a }: MemberRanked | CareTeamMemberRanked,
            { rank: b }: MemberRanked | CareTeamMemberRanked,
          ) => {
            if (a && b) return a - b;
            if (a) return -1;
            if (b) return 1;
            return 0;
          },
        );
    }
    setItems(result);
  };

  useEffect(() => {
    if (member && contactInfoRanks) {
      resetItems();
    } else {
      setItems([]);
    }
  }, [member, careTeamMembers, contactInfoRanks]);

  return (
    <Modal
      title={translate('memberProfile.header.contactInfoTitle')}
      maxWidth="lg"
      onClose={onClose}
      open={!!open}
      onCancel={
        editMode
          ? () => {
              resetItems();
              setEditMode(false);
            }
          : undefined
      }
      onSubmit={async () => {
        if (editMode) {
          await updateMemberContactInfoRank(
            member.id,
            items.map((item, idx) => ({
              id: item.rankId,
              careTeamMemberId:
                item instanceof CareTeamMember ? item.id : undefined,
              memberId: member.id,
              type:
                item instanceof CareTeamMember
                  ? MemberContactRankingType.CARE_TEAM_MEMBER.value
                  : MemberContactRankingType.MEMBER.value,
              ranking: idx + 1,
            })),
          );
        }
        setEditMode(!editMode);
        return false;
      }}
      cancelText={
        editMode ? translate('global.cancel') : translate('global.close')
      }
      submitType={editMode ? undefined : 'outlined'}
      submitText={
        editMode
          ? translate('global.save')
          : translate('memberProfile.header.contactInfoEdit')
      }
    >
      {editMode ? (
        <ContactInfoSortableList items={items} onChange={setItems} />
      ) : (
        items.map((item, index) =>
          item instanceof CareTeamMember ? (
            <ContactInfoCTMRow
              ctm={item}
              index={index}
              key={`contact-caregiver-${member.id}`}
            />
          ) : (
            <ContactInfoMemberRow
              member={member}
              index={index}
              key={`contact-member-${item.id}`}
            />
          ),
        )
      )}
    </Modal>
  );
};

export default ContactInfoModal;
